import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useRecoilState } from 'recoil';
import { userAtom, roleAtom, subAtom, outletAtom } from 'core/config/atoms';

import { Bed, BedSingle, Building2, CookingPot, CreditCard, HandPlatter, Percent, Printer, User } from 'lucide-react';

import { AnimatePresence, motion } from "framer-motion";

import PanelDev from '../panels/dev/PanelDev';

import PanelSettings from 'panels/settings/PanelSettings';
import PanelSettingOutlet from 'panels/settings/PanelSettingOutlet';
import PanelBilling from 'panels/settings/PanelBilling';
import PanelPrinters from 'panels/settings/PanelPrinters';
import PanelTax from 'panels/settings/PanelTax';
import PanelSettingsTable from 'panels/settings/PanelSettingsTable';
import PanelSettingsRoom from 'panels/settings/PanelSettingsRoom';
import PanelSettingsRoomType from 'panels/settings/PanelSettingsRoomType';
import PanelMenu from 'panels/settings/PanelMenu';
import PanelHostel from 'panels/settings/PanelHostel';
import PanelRental from 'panels/settings/PanelRental';

export default function LayoutSetting() {

    const [userData] = useRecoilState(userAtom);
    const [roleData] = useRecoilState(roleAtom);
    const [outlet] = useRecoilState(outletAtom);
    const [sub] = useRecoilState(subAtom);

    const itemVariants = {
        closed: { opacity: 0 },
        open: { opacity: 1 }
    };

    const { page } = useParams();

    const [isOpen, setIsOpen] = useState(true);

    const navigate = useNavigate();

    const changeNav = (link) => {
        navigate(`/${link}`);
        setIsOpen(true);
    }

    const toggleMenu = () => {
        setIsOpen(!isOpen);
    }

    const closeMenu = () => {
        setIsOpen(false);
    }

    const openMenu = () => {
        setIsOpen(true);
    }

    return (
        <div className='w-full min-h-screen grid grid-cols-12 gap-2'>
            <AnimatePresence>
                {
                    isOpen && <motion.div initial={{ flexBasis: 0, opacity: 0 }} animate={{ flexBasis: '100%', opacity: 1 }} exit={{ flexBasis: 0, opacity: 0, transition: { delay: 0.2, duration: 0.3 } }} className="col-span-2 h-screen bg-slate-100">
                        <motion.h1 initial="closed" animate="open" exit="closed" variants={itemVariants} className="text-2xl font-medium px-1 mx-2 py-1 my-2 rounded">Settings</motion.h1>
                        <motion.div initial="closed" animate="open" exit="closed" variants={itemVariants}>
                            <p className='text-sm uppercase px-1 mx-2'>{userData.fname} {userData.lname}</p>
                        </motion.div>
                        <motion.ul initial="closed" animate="open" exit="closed" variants={itemVariants} className="w-full flex flex-col items-start space-y-2 px-2 mt-2">
                            <li className={`w-full p-1 cursor-pointer text-base hover:bg-gray-200 rounded flex items-center ${page === 'setting' ? 'text-prime font-medium' : 'text-black'}`} onClick={() => { changeNav('setting') }}>
                                <User size={20} className='mr-2' /> User Profile
                            </li>
                            {
                                roleData.modules.includes("SETTINGS") && <li className={`w-full p-1 cursor-pointer text-base hover:bg-gray-200 rounded flex items-center ${page === 'company' ? 'text-prime font-medium' : 'text-black'}`} onClick={() => { changeNav('company') }}>
                                    <Building2 size={20} className='mr-2' /> Outlet Settings
                                </li>
                            }
                            {
                                roleData.modules.includes("SETTINGS") && <li className={`w-full p-1 cursor-pointer text-base hover:bg-gray-200 rounded flex items-center ${page === 'subscription' ? 'text-prime font-medium' : 'text-black'}`} onClick={() => { changeNav('subscription') }}>
                                    <CreditCard size={20} className='mr-2' /> Billing
                                </li>
                            }
                        </motion.ul>
                        <motion.div initial="closed" animate="open" exit="closed" variants={itemVariants}>
                            <p className='text-sm uppercase px-1 mx-2 mt-2'>OUTLET SETTINGS</p>
                        </motion.div>
                        <motion.ul initial="closed" animate="open" exit="closed" variants={itemVariants} className="w-full flex flex-col items-start space-y-2 px-2 mt-2">
                            {
                                roleData.modules.includes("SETTINGS") && <li className={`w-full p-1 cursor-pointer text-base hover:bg-gray-200 rounded flex items-center ${page === 'print' ? 'text-prime font-medium' : 'text-black'}`} onClick={() => { changeNav('print') }}>
                                    <Printer size={20} className='mr-2' /> Print Settings
                                </li>
                            }
                            {
                                (roleData.modules.includes("SETTINGS") && sub.features.isTax) && <li className={`w-full p-1 cursor-pointer text-base hover:bg-gray-200 rounded flex items-center ${page === 'tax' ? 'text-prime font-medium' : 'text-black'}`} onClick={() => { changeNav('tax') }}>
                                    <Percent size={20} className='mr-2' /> Tax Settings
                                </li>
                            }
                            {
                                (roleData.modules.includes("SETTINGS") && (outlet.outletid.terminaltype === 'standard' || outlet.outletid.terminaltype === 'cafe')) && <li className={`w-full p-1 cursor-pointer text-base hover:bg-gray-200 rounded flex items-center ${page === 'table' ? 'text-prime font-medium' : 'text-black'}`} onClick={() => { changeNav('table') }}>
                                    <HandPlatter size={20} className='mr-2' /> Table Setup
                                </li>
                            }
                            {
                                (roleData.modules.includes("SETTINGS") && roleData.modules.includes("BOOKING") && sub.features.isRoom && (outlet.outletid.terminaltype === 'standard' || outlet.outletid.terminaltype === 'cafe')) && <li className={`w-full p-1 cursor-pointer text-base hover:bg-gray-200 rounded flex items-center ${page === 'room' ? 'text-prime font-medium' : 'text-black'}`} onClick={() => { changeNav('room') }}>
                                    <BedSingle size={20} className='mr-2' /> Room Setup
                                </li>
                            }
                            {
                                (roleData.modules.includes("SETTINGS") && roleData.modules.includes("BOOKING") && sub.features.isRoom && (outlet.outletid.terminaltype === 'standard' || outlet.outletid.terminaltype === 'cafe')) && <li className={`w-full p-1 cursor-pointer text-base hover:bg-gray-200 rounded flex items-center ${page === 'roomType' ? 'text-prime font-medium' : 'text-black'}`} onClick={() => { changeNav('roomType') }}>
                                    <BedSingle size={20} className='mr-2' /> Room Type Setup
                                </li>
                            }
                            {
                                sub.apps.includes("APP00002") && <li className={`w-full p-1 cursor-pointer text-base hover:bg-gray-200 rounded flex items-center ${page === 'hostel' ? 'text-prime font-medium' : 'text-black'}`} onClick={() => { changeNav('hostel') }}>
                                    <Bed size={20} className='mr-2' /> Hostel Setup
                                </li>
                            }
                            {
                                sub.apps.includes("APP00004") && <li className={`w-full p-1 cursor-pointer text-base hover:bg-gray-200 rounded flex items-center ${page === 'rental' ? 'text-prime font-medium' : 'text-black'}`} onClick={() => { changeNav('rental') }}>
                                    <Building2 size={20} className='mr-2' /> Rentables Setup
                                </li>
                            }
                            {
                                roleData.modules.includes("SETTINGS") && <li className={`w-full p-1 cursor-pointer text-base hover:bg-gray-200 rounded flex items-center ${page === 'menu' ? 'text-prime font-medium' : 'text-black'}`} onClick={() => { changeNav('menu') }}>
                                    <CookingPot size={20} className='mr-2' /> Menu Setup
                                </li>
                            }
                        </motion.ul>
                    </motion.div>
                }
            </AnimatePresence>
            <div className={`h-screen ${isOpen ? "col-span-10" : "col-span-12"}`}>
                {
                    (() => {
                        switch (page) {
                            case 'setting':
                                return (<PanelSettings onToggle={toggleMenu} closeToggle={closeMenu} openToggle={openMenu} />);
                            case 'company':
                                return (<PanelSettingOutlet onToggle={toggleMenu} closeToggle={closeMenu} openToggle={openMenu} />);
                            case 'subscription':
                                return (<PanelBilling onToggle={toggleMenu} closeToggle={closeMenu} openToggle={openMenu} />);
                            case 'print':
                                return (<PanelPrinters onToggle={toggleMenu} closeToggle={closeMenu} openToggle={openMenu} />);
                            case 'tax':
                                return (<PanelTax onToggle={toggleMenu} closeToggle={closeMenu} openToggle={openMenu} />);
                            case 'table':
                                return (<PanelSettingsTable onToggle={toggleMenu} closeToggle={closeMenu} openToggle={openMenu} />);
                            case 'room':
                                return (<PanelSettingsRoom onToggle={toggleMenu} closeToggle={closeMenu} openToggle={openMenu} />);
                            case 'roomType':
                                return (<PanelSettingsRoomType onToggle={toggleMenu} closeToggle={closeMenu} openToggle={openMenu} />);
                            case 'menu':
                                return (<PanelMenu onToggle={toggleMenu} closeToggle={closeMenu} openToggle={openMenu} />);
                            case 'hostel':
                                return (<PanelHostel onToggle={toggleMenu} closeToggle={closeMenu} openToggle={openMenu} />);
                            case 'rental':
                                return (<PanelRental onToggle={toggleMenu} closeToggle={closeMenu} openToggle={openMenu} />);
                            default:
                                return (<PanelDev />);
                        }
                    })()
                }
            </div>
        </div>
    )
}
